import { useCallback, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/**
 * A list that grows as it is scrolled, over an endpoint that pages.
 *
 * **The server still does the sorting and the filtering, over the whole set.**
 * A batch is `limit` and `offset` against the same route the pager used, so the
 * order is the server's order and a filter narrows everything, not the rows
 * already on screen. Changing any filter or sort changes `key`, which is a
 * different query with its own cache entry: the accumulated rows are discarded
 * and the list starts again at the top, in the new order. There is no code path
 * that reorders what is loaded.
 *
 * **The counts and the filter options come from the first batch.** Every one of
 * these endpoints returns them beside the rows, computed over the whole set, so
 * they are right from the first batch and do not move as more arrive.
 */
export interface InfiniteList<TRow, TPage> {
  /** Every row loaded so far, in the server's order. */
  rows: TRow[];
  /** The first batch's response: the counts, the filter options, the total. */
  head: TPage | undefined;
  total: number;
  /** True while any batch is in flight, including the first. */
  loading: boolean;
  /** True only while the next batch is being appended. */
  appending: boolean;
  /** Nothing has answered yet. */
  pending: boolean;
  error: Error | null;
  failureReason: Error | null;
  isPlaceholderData: boolean;
  dataUpdatedAt: number;
  refetch: () => void;
  /** Ask for one more batch. Safe to call repeatedly; ignored while one is in flight. */
  loadMore: () => void;
  hasMore: boolean;
  /**
   * Keep loading batches until `found` is true of the rows loaded, or the list
   * runs out. Resolves with whether it was found, and how many batches it took.
   */
  loadUntil: (found: (rows: TRow[]) => boolean, limit?: number) => Promise<{ found: boolean; batches: number }>;
}

export function useInfiniteList<TRow, TPage extends { total: number }>(options: {
  /** Identifies the query: the endpoint and every filter and sort in it. */
  key: unknown[];
  /** Builds the path for one batch. */
  path: (batch: { limit: number; offset: number }) => string;
  rowsOf: (page: TPage) => TRow[];
  batchSize: number;
  /**
   * What `loadUntil` asks for at a time, where `batchSize` is what scrolling
   * asks for. Bounded by the endpoint's own cap: see `maxLimit` on the listing
   * specs, and the note there on why a request costs the same either way.
   */
  slabSize?: number;
  enabled?: boolean;
}): InfiniteList<TRow, TPage> {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const { key, path, rowsOf, batchSize, slabSize = batchSize, enabled = true } = options;
  // Hashed by value, so rebuilding it each render still names the same query.
  const queryKey = ['infinite', ...key, batchSize];

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => apiFetch<TPage>(path({ limit: batchSize, offset: pageParam }), getToken),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + rowsOf(p).length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    ...queryDefaults,
    enabled,
  });

  const pages = query.data?.pages ?? [];
  const rows = useMemo(() => pages.flatMap((p) => rowsOf(p)), [pages, rowsOf]);
  const head = pages[0];

  // fetchNextPage is safe to call while one is in flight — react-query ignores
  // it — but keeping the guard here makes the sentinel's job obvious.
  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
  }, [query]);

  // The live values, for loadUntil's loop: it runs outside React's render, so it
  // cannot close over this render's copies.
  const live = useRef(query);
  live.current = query;
  const liveRows = useRef(rows);
  liveRows.current = rows;

  const loadUntil = useCallback(async (found: (rows: TRow[]) => boolean, max = 40) => {
    let batches = 0;
    if (found(liveRows.current)) return { found: true, batches };

    // This is loading to a position, not browsing, so it asks for a slab rather
    // than a batch. A request costs what the scan costs — about the same whether
    // it carries fifty rows or a thousand, because row security is paid per row
    // scanned — so the wait is the number of requests, not the number of rows.
    // The median row of the filed view is reached in one request; the last in
    // three slabs, where it would be fifty-five batches.
    type Cached = { pages: TPage[]; pageParams: number[] };
    while (batches < max) {
      const cached = queryClient.getQueryData<Cached>(queryKey);
      // Nothing has answered yet: let the query itself open the list.
      if (!cached?.pages.length) {
        await live.current.fetchNextPage();
        batches += 1;
        if (found(liveRows.current)) return { found: true, batches };
        continue;
      }
      const loaded = cached.pages.reduce((n, p) => n + rowsOf(p).length, 0);
      const total = cached.pages[0]?.total ?? 0;
      if (loaded >= total) break;
      const page = await apiFetch<TPage>(path({ limit: slabSize, offset: loaded }), getToken);
      const grown: Cached = { pages: [...cached.pages, page], pageParams: [...cached.pageParams, loaded] };
      queryClient.setQueryData(queryKey, grown);
      batches += 1;
      // Read the rows from the cache just written rather than from the last
      // render: React has not re-rendered inside this loop.
      if (found(grown.pages.flatMap((p) => rowsOf(p)))) return { found: true, batches };
    }
    return { found: found(liveRows.current), batches };
  }, [queryClient, queryKey, path, rowsOf, slabSize, getToken]);

  return {
    rows,
    head,
    total: head?.total ?? 0,
    loading: query.isFetching,
    appending: query.isFetchingNextPage,
    pending: query.isPending,
    error: query.error as Error | null,
    failureReason: query.failureReason as Error | null,
    isPlaceholderData: query.isPlaceholderData,
    dataUpdatedAt: query.dataUpdatedAt,
    refetch: () => void query.refetch(),
    loadMore,
    hasMore: query.hasNextPage,
    loadUntil,
  };
}

/**
 * Where a list was when it was left, so returning to it does not lose the place.
 *
 * Keyed by the filter set, not by the route: coming back to a different filter
 * is a different list and starts at the top, which is what a changed filter
 * means. Kept in memory for the session only — a reload is a fresh list, and
 * restoring a scroll position into rows that may have changed underneath would
 * be a worse lie than starting at the top.
 */
export interface Place {
  scrollY: number;
  /**
   * How many ROWS were mounted, not how many appends it took to mount them.
   *
   * This was a count of appends, and the two are not the same currency. The
   * quick search loads a slab of a thousand rows as a single append, so a list
   * of 747 rows was remembered as "one", and coming back replayed it as one
   * `loadMore()` — rebuilding 150 rows out of 747 and calling it restored. In
   * the other direction a deep scroll recorded a count larger than anything the
   * fresh list had, and the restore called `loadMore()` on every render trying
   * to reach it, a batch a second, until the whole list was mounted.
   */
  rows: number;
}

const places = new Map<string, Place>();

export function rememberPlace(key: string, scrollY: number, rows: number): void {
  places.set(key, { scrollY, rows });
}

/**
 * Read the place and REMOVE it: a target is consumed exactly once.
 *
 * Leaving it behind is what made a single bad entry permanent. The entry was
 * rewritten on every scroll and every append, including during a restore, so
 * once it held a top-of-page position every later visit restored to the top and
 * re-recorded the same thing. Taking it means a restore that has happened
 * cannot happen again, and a mount with nothing to restore does nothing at all.
 */
export function takePlace(key: string): Place | undefined {
  const place = places.get(key);
  places.delete(key);
  return place;
}

/** Without consuming it. For deciding whether a restore is pending. */
export function placeFor(key: string): Place | undefined {
  return places.get(key);
}
